import React, { useEffect } from 'react';

const AppLevelCache = () => {
  useEffect(() => {
    // SEO optimization
    document.title = 'Application-Level Caching with Redis and Memcached - Complete Guide 2024';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Master application-level caching with Redis and Memcached. Learn caching patterns, data structures, and performance optimization techniques.');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = 'Master application-level caching with Redis and Memcached. Learn caching patterns, data structures, and performance optimization techniques.';
      document.getElementsByTagName('head')[0].appendChild(meta);
    }

    return () => {
      document.title = 'SlazySloth - Deep Dives';
    };
  }, []);

  const styles = {
    container: {
      width: '100%',
      margin: 0,
      padding: '2rem',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
      lineHeight: 1.6,
      color: '#333',
      background: '#ffffff',
      fontSize: '16px'
    },
    header: {
      marginBottom: '2rem'
    },
    title: {
      fontSize: '16px',
      fontWeight: 600,
      color: '#333',
      marginBottom: '1rem'
    },
    subtitle: {
      fontSize: '16px',
      color: '#333',
      marginBottom: '1rem',
      fontWeight: 400
    },
    content: {
      fontSize: '16px',
      lineHeight: 1.6
    },
    heading: {
      fontSize: '16px',
      fontWeight: 600,
      color: '#333',
      margin: '1.5rem 0 1rem 0'
    },
    paragraph: {
      marginBottom: '1rem',
      color: '#333',
      fontSize: '16px',
      lineHeight: 1.6
    },
    list: {
      marginBottom: '1rem',
      paddingLeft: '1.5rem'
    },
    listItem: {
      marginBottom: '0.5rem',
      color: '#333',
      fontSize: '16px',
      lineHeight: 1.6
    },
    codeBlock: {
      background: '#f5f5f5',
      padding: '1rem',
      fontFamily: 'monospace',
      fontSize: '14px',
      color: '#333',
      marginBottom: '1rem',
      whiteSpace: 'pre-wrap',
      overflow: 'auto'
    }
  };

  return (
    <article style={styles.container}>
      <header style={styles.header}>
        <h1 style={styles.title}>Application-Level Caching with Redis and Memcached</h1>
        <p style={styles.subtitle}>
          Boost application performance with in-memory caching solutions
        </p>
      </header>

      <main style={styles.content}>
        <section>
          <h2 style={styles.heading}>What is Application-Level Caching?</h2>
          <p style={styles.paragraph}>
            Application-level caching involves storing frequently accessed data in fast, in-memory storage systems 
            like Redis or Memcached. This reduces database load, improves response times, and enhances overall 
            application performance by serving data from memory instead of slower persistent storage.
          </p>
          <p style={styles.paragraph}>
            Unlike browser or CDN caching, application-level caches are controlled by your application code and 
            can store any type of data - from database query results to computed values, session data, and 
            temporary state information.
          </p>
        </section>

        <section>
          <h2 style={styles.heading}>Redis vs Memcached</h2>
          
          <h3 style={styles.heading}>Redis Features</h3>
          <p style={styles.paragraph}>
            Redis (Remote Dictionary Server) is an advanced in-memory data structure store that supports various 
            data types and offers persistence, replication, and advanced features.
          </p>
          <pre style={styles.codeBlock}>
{`Redis Key Features:

Data Types:
- Strings: Simple key-value pairs
- Lists: Ordered collections of strings
- Sets: Unordered collections of unique strings
- Sorted Sets: Ordered sets with scores
- Hashes: Field-value pairs (like objects)
- Streams: Log-like data structure
- Bitmaps: Bit-level operations
- HyperLogLog: Probabilistic cardinality estimation

Advanced Features:
✓ Persistence (RDB snapshots, AOF logs)
✓ Master-slave replication
✓ Clustering for horizontal scaling
✓ Pub/Sub messaging
✓ Lua scripting
✓ Transactions with MULTI/EXEC
✓ Expiration and eviction policies
✓ Geospatial indexing

Use Cases:
- Session storage
- Real-time analytics
- Leaderboards and counting
- Message queues
- Distributed locks
- Rate limiting`}
          </pre>

          <h3 style={styles.heading}>Memcached Features</h3>
          <p style={styles.paragraph}>
            Memcached is a simple, high-performance distributed memory caching system designed for speed and 
            simplicity with a focus on key-value storage.
          </p>
          <pre style={styles.codeBlock}>
{`Memcached Key Features:

Characteristics:
- Simple key-value store
- Multi-threaded architecture
- Distributed by design
- No persistence
- LRU eviction only
- Binary and text protocols

Advantages:
✓ Extremely fast (lower latency)
✓ Lower memory overhead
✓ Simple to deploy and manage
✓ Multi-threaded (better CPU utilization)
✓ Mature and stable

Limitations:
✗ Only supports strings
✗ No persistence
✗ No replication
✗ No complex data structures
✗ Limited eviction policies

Use Cases:
- Database query caching
- Page fragment caching
- Object caching
- Session storage (simple)
- API response caching

When to Choose:
Redis: Complex data types, persistence, advanced features
Memcached: Simple caching, maximum performance, minimal overhead`}
          </pre>
        </section>

        <section>
          <h2 style={styles.heading}>Redis Implementation Examples</h2>
          
          <h3 style={styles.heading}>Basic Redis Operations</h3>
          <p style={styles.paragraph}>
            Here are fundamental Redis operations for common caching scenarios:
          </p>
          <pre style={styles.codeBlock}>
{`Redis Basic Operations:

# String operations
SET user:1000 "John Doe"
GET user:1000
SETEX session:abc123 3600 "user_data"  # Set with 1-hour expiration
INCR page_views:homepage
INCRBY user:1000:points 100

# Hash operations (perfect for objects)
HSET user:1000 name "John Doe" email "john@example.com" age 30
HGET user:1000 name
HGETALL user:1000
HINCRBY user:1000 login_count 1

# List operations (for queues, recent items)
LPUSH recent_orders order:123 order:124
LRANGE recent_orders 0 9  # Get 10 most recent
RPOP task_queue  # Process tasks

# Set operations (for tags, unique items)
SADD user:1000:tags "python" "redis" "caching"
SMEMBERS user:1000:tags
SINTER user:1000:tags user:2000:tags  # Common tags

# Sorted Set operations (for leaderboards, rankings)
ZADD leaderboard 1500 "player1" 1200 "player2"
ZREVRANGE leaderboard 0 9 WITHSCORES  # Top 10 players
ZINCRBY leaderboard 100 "player1"

Python Redis Example:
import redis
import json
from datetime import timedelta

class RedisCache:
    def __init__(self, host='localhost', port=6379, db=0):
        self.redis_client = redis.Redis(host=host, port=port, db=db, decode_responses=True)
    
    def set(self, key, value, ttl=None):
        """Set a value with optional TTL"""
        if isinstance(value, (dict, list)):
            value = json.dumps(value)
        
        if ttl:
            self.redis_client.setex(key, ttl, value)
        else:
            self.redis_client.set(key, value)
    
    def get(self, key):
        """Get a value and try to parse as JSON"""
        value = self.redis_client.get(key)
        if value is None:
            return None
        
        try:
            return json.loads(value)
        except json.JSONDecodeError:
            return value
    
    def delete(self, key):
        """Delete a key"""
        return self.redis_client.delete(key)
    
    def exists(self, key):
        """Check if key exists"""
        return self.redis_client.exists(key)

# Usage
cache = RedisCache()
cache.set('user:1000', {'name': 'John', 'email': 'john@example.com'}, ttl=3600)
user_data = cache.get('user:1000')`}
          </pre>

          <h3 style={styles.heading}>Advanced Redis Patterns</h3>
          <p style={styles.paragraph}>
            Implement sophisticated caching patterns using Redis advanced features:
          </p>
          <pre style={styles.codeBlock}>
{`Advanced Redis Patterns:

1. Cache-Aside Pattern:
class DatabaseWithCache:
    def __init__(self, db_connection, redis_client):
        self.db = db_connection
        self.cache = redis_client
    
    def get_user(self, user_id):
        cache_key = f"user:{user_id}"
        
        # Try cache first
        cached_user = self.cache.get(cache_key)
        if cached_user:
            return json.loads(cached_user)
        
        # Cache miss - fetch from database
        user = self.db.execute("SELECT * FROM users WHERE id = %s", [user_id])
        
        if user:
            # Store in cache for 1 hour
            self.cache.setex(cache_key, 3600, json.dumps(user))
        
        return user
    
    def update_user(self, user_id, user_data):
        # Update database
        self.db.execute("UPDATE users SET ... WHERE id = %s", [user_id])
        
        # Invalidate cache
        self.cache.delete(f"user:{user_id}")

2. Write-Through Pattern:
class WriteThroughCache:
    def update_user(self, user_id, user_data):
        # Update database first
        self.db.execute("UPDATE users SET ... WHERE id = %s", [user_id])
        
        # Update cache
        cache_key = f"user:{user_id}"
        self.cache.setex(cache_key, 3600, json.dumps(user_data))

3. Write-Behind (Write-Back) Pattern:
class WriteBehindCache:
    def __init__(self):
        self.dirty_keys = set()
        self.background_writer_running = True
        threading.Thread(target=self.background_writer, daemon=True).start()
    
    def update_user(self, user_id, user_data):
        # Update cache immediately
        cache_key = f"user:{user_id}"
        self.cache.setex(cache_key, 3600, json.dumps(user_data))
        
        # Mark as dirty for background writing
        self.dirty_keys.add(user_id)
    
    def background_writer(self):
        while self.background_writer_running:
            if self.dirty_keys:
                user_id = self.dirty_keys.pop()
                user_data = self.cache.get(f"user:{user_id}")
                if user_data:
                    self.db.execute("UPDATE users SET ... WHERE id = %s", [user_id])
            time.sleep(1)

4. Distributed Lock Pattern:
import uuid
import time

class RedisLock:
    def __init__(self, redis_client, key, timeout=10):
        self.redis = redis_client
        self.key = f"lock:{key}"
        self.timeout = timeout
        self.identifier = str(uuid.uuid4())
    
    def acquire(self):
        end = time.time() + self.timeout
        while time.time() < end:
            if self.redis.set(self.key, self.identifier, nx=True, ex=self.timeout):
                return True
            time.sleep(0.001)
        return False
    
    def release(self):
        # Use Lua script to ensure atomic check-and-delete
        lua_script = """
        if redis.call("GET", KEYS[1]) == ARGV[1] then
            return redis.call("DEL", KEYS[1])
        else
            return 0
        end
        """
        return self.redis.eval(lua_script, 1, self.key, self.identifier)

# Usage
lock = RedisLock(redis_client, "critical_section")
if lock.acquire():
    try:
        # Critical section code
        pass
    finally:
        lock.release()`}
          </pre>
        </section>

        <section>
          <h2 style={styles.heading}>Memcached Implementation</h2>
          
          <h3 style={styles.heading}>Basic Memcached Operations</h3>
          <p style={styles.paragraph}>
            Memcached provides simple but effective caching operations focused on key-value storage:
          </p>
          <pre style={styles.codeBlock}>
{`Memcached Operations:

Python with pymemcache:
from pymemcache.client.base import Client
import json
import hashlib

class MemcachedCache:
    def __init__(self, servers=['127.0.0.1:11211']):
        if len(servers) == 1:
            self.client = Client(servers[0])
        else:
            # Use consistent hashing for multiple servers
            from pymemcache.client.hash import HashClient
            self.client = HashClient(servers)
    
    def _make_key(self, key):
        """Ensure key is valid (max 250 chars, no spaces)"""
        if len(key) > 250 or ' ' in key:
            return hashlib.md5(key.encode()).hexdigest()
        return key
    
    def set(self, key, value, ttl=0):
        """Set a value with TTL (0 = no expiration)"""
        key = self._make_key(key)
        if isinstance(value, (dict, list)):
            value = json.dumps(value)
        return self.client.set(key, value, expire=ttl)
    
    def get(self, key):
        """Get a value"""
        key = self._make_key(key)
        value = self.client.get(key)
        if value is None:
            return None
        
        # Try to parse as JSON
        if isinstance(value, bytes):
            value = value.decode('utf-8')
        
        try:
            return json.loads(value)
        except (json.JSONDecodeError, TypeError):
            return value
    
    def get_multi(self, keys):
        """Get multiple values at once"""
        key_mapping = {self._make_key(k): k for k in keys}
        results = self.client.get_multi(list(key_mapping.keys()))
        
        # Map back to original keys
        return {key_mapping[k]: v for k, v in results.items()}
    
    def delete(self, key):
        """Delete a key"""
        key = self._make_key(key)
        return self.client.delete(key)
    
    def increment(self, key, delta=1):
        """Increment a numeric value"""
        key = self._make_key(key)
        return self.client.incr(key, delta)
    
    def flush_all(self):
        """Clear all cached data"""
        return self.client.flush_all()

# Usage
cache = MemcachedCache()
cache.set('user:1000', {'name': 'John', 'email': 'john@example.com'}, ttl=3600)
user_data = cache.get('user:1000')

# Batch operations for better performance
user_ids = ['user:1000', 'user:1001', 'user:1002']
users = cache.get_multi(user_ids)`}
          </pre>

          <h3 style={styles.heading}>Memcached Consistent Hashing</h3>
          <p style={styles.paragraph}>
            For multiple Memcached servers, implement consistent hashing to distribute keys evenly and handle 
            server failures gracefully:
          </p>
          <pre style={styles.codeBlock}>
{`Consistent Hashing Implementation:

import hashlib
import bisect

class ConsistentHashRing:
    def __init__(self, servers, replicas=150):
        self.replicas = replicas
        self.ring = {}
        self.sorted_keys = []
        
        for server in servers:
            self.add_server(server)
    
    def add_server(self, server):
        """Add a server to the hash ring"""
        for i in range(self.replicas):
            key = self._hash(f"{server}:{i}")
            self.ring[key] = server
            bisect.insort(self.sorted_keys, key)
    
    def remove_server(self, server):
        """Remove a server from the hash ring"""
        for i in range(self.replicas):
            key = self._hash(f"{server}:{i}")
            if key in self.ring:
                del self.ring[key]
                self.sorted_keys.remove(key)
    
    def get_server(self, key):
        """Get the server for a given key"""
        if not self.ring:
            return None
        
        hash_key = self._hash(key)
        idx = bisect.bisect_right(self.sorted_keys, hash_key)
        
        if idx == len(self.sorted_keys):
            idx = 0
        
        return self.ring[self.sorted_keys[idx]]
    
    def _hash(self, key):
        """Hash function for the ring"""
        return int(hashlib.md5(key.encode()).hexdigest(), 16)

class DistributedMemcachedCache:
    def __init__(self, servers):
        self.hash_ring = ConsistentHashRing(servers)
        self.clients = {}
        
        # Create client connections
        for server in servers:
            self.clients[server] = Client(server)
    
    def _get_client(self, key):
        """Get the appropriate client for a key"""
        server = self.hash_ring.get_server(key)
        return self.clients[server]
    
    def set(self, key, value, ttl=0):
        client = self._get_client(key)
        return client.set(key, value, expire=ttl)
    
    def get(self, key):
        client = self._get_client(key)
        return client.get(key)
    
    def delete(self, key):
        client = self._get_client(key)
        return client.delete(key)

# Usage with multiple servers
servers = ['127.0.0.1:11211', '127.0.0.1:11212', '127.0.0.1:11213']
cache = DistributedMemcachedCache(servers)`}
          </pre>
        </section>

        <section>
          <h2 style={styles.heading}>Caching Strategies and Patterns</h2>
          
          <h3 style={styles.heading}>Cache Warming</h3>
          <p style={styles.paragraph}>
            Proactively populate cache with frequently accessed data to avoid cache misses during peak traffic:
          </p>
          <pre style={styles.codeBlock}>
{`Cache Warming Strategies:

1. Scheduled Cache Warming:
import schedule
import time

class CacheWarmer:
    def __init__(self, cache, database):
        self.cache = cache
        self.db = database
    
    def warm_popular_products(self):
        """Warm cache with popular products"""
        popular_products = self.db.execute("""
            SELECT product_id, COUNT(*) as views 
            FROM product_views 
            WHERE created_at > NOW() - INTERVAL 24 HOUR
            GROUP BY product_id 
            ORDER BY views DESC 
            LIMIT 100
        """)
        
        for product in popular_products:
            product_data = self.db.get_product(product['product_id'])
            cache_key = f"product:{product['product_id']}"
            self.cache.set(cache_key, product_data, ttl=3600)
        
        print(f"Warmed cache with {len(popular_products)} popular products")
    
    def warm_user_sessions(self):
        """Warm cache with active user sessions"""
        active_users = self.db.execute("""
            SELECT DISTINCT user_id 
            FROM user_sessions 
            WHERE last_activity > NOW() - INTERVAL 1 HOUR
        """)
        
        for user in active_users:
            user_data = self.db.get_user(user['user_id'])
            cache_key = f"user:{user['user_id']}"
            self.cache.set(cache_key, user_data, ttl=1800)

# Schedule cache warming
warmer = CacheWarmer(cache, database)
schedule.every(30).minutes.do(warmer.warm_popular_products)
schedule.every(15).minutes.do(warmer.warm_user_sessions)

2. Application Startup Warming:
class Application:
    def __init__(self):
        self.cache = RedisCache()
        self.db = Database()
        self.warm_critical_data()
    
    def warm_critical_data(self):
        """Warm cache with critical data on startup"""
        # Warm configuration data
        config = self.db.get_application_config()
        self.cache.set('app:config', config, ttl=86400)
        
        # Warm lookup tables
        categories = self.db.get_all_categories()
        self.cache.set('categories:all', categories, ttl=3600)
        
        # Warm frequently accessed reference data
        currencies = self.db.get_supported_currencies()
        self.cache.set('currencies:supported', currencies, ttl=86400)

3. Predictive Cache Warming:
class PredictiveCacheWarmer:
    def __init__(self, cache, ml_model):
        self.cache = cache
        self.model = ml_model
    
    def predict_and_warm(self, user_id):
        """Predict what user might access and warm cache"""
        predictions = self.model.predict_user_interests(user_id)
        
        for item_id, confidence in predictions:
            if confidence > 0.7:  # High confidence threshold
                item_data = self.db.get_item(item_id)
                cache_key = f"item:{item_id}"
                # Shorter TTL for predictions
                self.cache.set(cache_key, item_data, ttl=1800)`}
          </pre>

          <h3 style={styles.heading}>Cache Invalidation</h3>
          <p style={styles.paragraph}>
            Implement smart cache invalidation strategies to ensure data consistency while maintaining performance:
          </p>
          <pre style={styles.codeBlock}>
{`Cache Invalidation Patterns:

1. Tag-Based Invalidation:
class TaggedCache:
    def __init__(self, redis_client):
        self.redis = redis_client
    
    def set_with_tags(self, key, value, tags, ttl=None):
        """Set value with associated tags"""
        # Store the main data
        if ttl:
            self.redis.setex(key, ttl, json.dumps(value))
        else:
            self.redis.set(key, json.dumps(value))
        
        # Associate key with tags
        for tag in tags:
            tag_key = f"tag:{tag}"
            self.redis.sadd(tag_key, key)
            if ttl:
                self.redis.expire(tag_key, ttl + 60)  # Tags live slightly longer
    
    def invalidate_by_tag(self, tag):
        """Invalidate all keys with a specific tag"""
        tag_key = f"tag:{tag}"
        keys = self.redis.smembers(tag_key)
        
        if keys:
            # Delete all keys with this tag
            self.redis.delete(*keys)
            # Delete the tag set
            self.redis.delete(tag_key)
        
        return len(keys)

# Usage
cache = TaggedCache(redis_client)
cache.set_with_tags(
    'product:123', 
    product_data, 
    tags=['product', 'category:electronics', 'brand:apple'],
    ttl=3600
)

# Invalidate all Apple products
cache.invalidate_by_tag('brand:apple')

2. Event-Driven Invalidation:
class EventDrivenCache:
    def __init__(self, cache, event_bus):
        self.cache = cache
        self.event_bus = event_bus
        
        # Subscribe to relevant events
        self.event_bus.subscribe('user.updated', self.on_user_updated)
        self.event_bus.subscribe('product.updated', self.on_product_updated)
        self.event_bus.subscribe('inventory.changed', self.on_inventory_changed)
    
    def on_user_updated(self, event):
        """Handle user update events"""
        user_id = event['user_id']
        
        # Invalidate user-specific caches
        self.cache.delete(f"user:{user_id}")
        self.cache.delete(f"user:{user_id}:preferences")
        self.cache.delete(f"user:{user_id}:recommendations")
    
    def on_product_updated(self, event):
        """Handle product update events"""
        product_id = event['product_id']
        category_id = event.get('category_id')
        
        # Invalidate product caches
        self.cache.delete(f"product:{product_id}")
        
        if category_id:
            self.cache.delete(f"category:{category_id}:products")
    
    def on_inventory_changed(self, event):
        """Handle inventory changes"""
        product_id = event['product_id']
        
        # Invalidate availability caches
        self.cache.delete(f"product:{product_id}:availability")
        self.cache.delete(f"inventory:low_stock")

3. Time-Based Invalidation with Refresh:
class RefreshingCache:
    def __init__(self, cache, refresh_interval=300):
        self.cache = cache
        self.refresh_interval = refresh_interval
        self.refresh_tasks = {}
    
    def get_with_refresh(self, key, refresh_func, *args, **kwargs):
        """Get value and schedule refresh if needed"""
        value = self.cache.get(key)
        
        if value is None:
            # Cache miss - fetch immediately
            value = refresh_func(*args, **kwargs)
            self.cache.set(key, value, ttl=self.refresh_interval * 2)
        else:
            # Check if refresh is needed
            refresh_key = f"{key}:refresh_time"
            last_refresh = self.cache.get(refresh_key)
            
            if not last_refresh or time.time() - last_refresh > self.refresh_interval:
                # Schedule background refresh
                self.schedule_refresh(key, refresh_func, args, kwargs)
        
        return value
    
    def schedule_refresh(self, key, refresh_func, args, kwargs):
        """Schedule background refresh"""
        if key not in self.refresh_tasks:
            def refresh_task():
                try:
                    new_value = refresh_func(*args, **kwargs)
                    self.cache.set(key, new_value, ttl=self.refresh_interval * 2)
                    self.cache.set(f"{key}:refresh_time", time.time(), ttl=self.refresh_interval * 2)
                finally:
                    self.refresh_tasks.pop(key, None)
            
            self.refresh_tasks[key] = threading.Thread(target=refresh_task)
            self.refresh_tasks[key].start()`}
          </pre>
        </section>

        <section>
          <h2 style={styles.heading}>Performance Optimization</h2>
          
          <h3 style={styles.heading}>Connection Pooling</h3>
          <p style={styles.paragraph}>
            Use connection pooling to reduce the overhead of establishing connections and improve performance:
          </p>
          <pre style={styles.codeBlock}>
{`Connection Pooling:

Redis Connection Pool:
import redis
from redis.connection import ConnectionPool

class OptimizedRedisCache:
    def __init__(self, host='localhost', port=6379, max_connections=50):
        self.pool = ConnectionPool(
            host=host,
            port=port,
            max_connections=max_connections,
            retry_on_timeout=True,
            health_check_interval=30
        )
        self.redis = redis.Redis(connection_pool=self.pool, decode_responses=True)
    
    def get_pool_stats(self):
        """Get connection pool statistics"""
        return {
            'created_connections': self.pool.created_connections,
            'available_connections': len(self.pool._available_connections),
            'in_use_connections': len(self.pool._in_use_connections)
        }

Memcached Connection Pool:
from pymemcache.client.base import PooledClient

class OptimizedMemcachedCache:
    def __init__(self, server='127.0.0.1:11211', max_pool_size=50):
        self.client = PooledClient(
            server,
            max_pool_size=max_pool_size,
            connect_timeout=5,
            timeout=5
        )
    
    def get_stats(self):
        """Get memcached server statistics"""
        return self.client.stats()

Circuit Breaker Pattern:
import time
from enum import Enum

class CircuitState(Enum):
    CLOSED = "closed"
    OPEN = "open"
    HALF_OPEN = "half_open"

class CircuitBreaker:
    def __init__(self, failure_threshold=5, timeout=60):
        self.failure_threshold = failure_threshold
        self.timeout = timeout
        self.failure_count = 0
        self.last_failure_time = None
        self.state = CircuitState.CLOSED
    
    def call(self, func, *args, **kwargs):
        """Execute function with circuit breaker protection"""
        if self.state == CircuitState.OPEN:
            if time.time() - self.last_failure_time > self.timeout:
                self.state = CircuitState.HALF_OPEN
            else:
                raise Exception("Circuit breaker is OPEN")
        
        try:
            result = func(*args, **kwargs)
            self.on_success()
            return result
        except Exception as e:
            self.on_failure()
            raise e
    
    def on_success(self):
        """Handle successful operation"""
        self.failure_count = 0
        self.state = CircuitState.CLOSED
    
    def on_failure(self):
        """Handle failed operation"""
        self.failure_count += 1
        self.last_failure_time = time.time()
        
        if self.failure_count >= self.failure_threshold:
            self.state = CircuitState.OPEN

# Usage with cache
cache_breaker = CircuitBreaker(failure_threshold=3, timeout=30)

def safe_cache_get(key):
    try:
        return cache_breaker.call(cache.get, key)
    except Exception:
        # Fallback to database
        return database.get(key)`}
          </pre>

          <h3 style={styles.heading}>Serialization Optimization</h3>
          <p style={styles.paragraph}>
            Choose efficient serialization formats to reduce memory usage and improve performance:
          </p>
          <pre style={styles.codeBlock}>
{`Serialization Comparison:

import json
import pickle
import msgpack
import time

class SerializationBenchmark:
    def __init__(self, data):
        self.data = data
    
    def benchmark_json(self):
        start = time.time()
        serialized = json.dumps(self.data)
        json.loads(serialized)
        return time.time() - start, len(serialized)
    
    def benchmark_pickle(self):
        start = time.time()
        serialized = pickle.dumps(self.data)
        pickle.loads(serialized)
        return time.time() - start, len(serialized)
    
    def benchmark_msgpack(self):
        start = time.time()
        serialized = msgpack.packb(self.data)
        msgpack.unpackb(serialized)
        return time.time() - start, len(serialized)

# Performance comparison for typical data
test_data = {
    'users': [{'id': i, 'name': f'User{i}', 'active': True} for i in range(1000)],
    'metadata': {'timestamp': time.time(), 'version': '1.0'}
}

benchmark = SerializationBenchmark(test_data)

json_time, json_size = benchmark.benchmark_json()
pickle_time, pickle_size = benchmark.benchmark_pickle()
msgpack_time, msgpack_size = benchmark.benchmark_msgpack()

print(f"JSON: {json_time:.4f}s, {json_size} bytes")
print(f"Pickle: {pickle_time:.4f}s, {pickle_size} bytes")
print(f"MessagePack: {msgpack_time:.4f}s, {msgpack_size} bytes")

Optimized Cache with MessagePack:
import msgpack

class OptimizedCache:
    def __init__(self, redis_client):
        self.redis = redis_client
    
    def set(self, key, value, ttl=None):
        """Set value using MessagePack serialization"""
        serialized = msgpack.packb(value, use_bin_type=True)
        
        if ttl:
            self.redis.setex(key, ttl, serialized)
        else:
            self.redis.set(key, serialized)
    
    def get(self, key):
        """Get value and deserialize with MessagePack"""
        value = self.redis.get(key)
        if value is None:
            return None
        
        return msgpack.unpackb(value, raw=False)
    
    def mget(self, keys):
        """Get multiple values efficiently"""
        values = self.redis.mget(keys)
        return [msgpack.unpackb(v, raw=False) if v else None for v in values]
    
    def mset(self, mapping, ttl=None):
        """Set multiple values efficiently"""
        serialized_mapping = {
            k: msgpack.packb(v, use_bin_type=True) 
            for k, v in mapping.items()
        }
        
        if ttl:
            pipe = self.redis.pipeline()
            for key, value in serialized_mapping.items():
                pipe.setex(key, ttl, value)
            pipe.execute()
        else:
            self.redis.mset(serialized_mapping)`}
          </pre>
        </section>

        <section>
          <h2 style={styles.heading}>Monitoring and Metrics</h2>
          
          <h3 style={styles.heading}>Cache Performance Metrics</h3>
          <p style={styles.paragraph}>
            Monitor key metrics to optimize cache performance and identify issues:
          </p>
          <pre style={styles.codeBlock}>
{`Cache Monitoring:

class CacheMonitor:
    def __init__(self, cache):
        self.cache = cache
        self.metrics = {
            'hits': 0,
            'misses': 0,
            'sets': 0,
            'deletes': 0,
            'errors': 0
        }
    
    def get_with_metrics(self, key):
        """Get value and track metrics"""
        try:
            value = self.cache.get(key)
            if value is not None:
                self.metrics['hits'] += 1
            else:
                self.metrics['misses'] += 1
            return value
        except Exception as e:
            self.metrics['errors'] += 1
            raise e
    
    def set_with_metrics(self, key, value, ttl=None):
        """Set value and track metrics"""
        try:
            result = self.cache.set(key, value, ttl)
            self.metrics['sets'] += 1
            return result
        except Exception as e:
            self.metrics['errors'] += 1
            raise e
    
    def get_hit_ratio(self):
        """Calculate cache hit ratio"""
        total_reads = self.metrics['hits'] + self.metrics['misses']
        if total_reads == 0:
            return 0
        return self.metrics['hits'] / total_reads
    
    def get_stats(self):
        """Get comprehensive cache statistics"""
        return {
            **self.metrics,
            'hit_ratio': self.get_hit_ratio(),
            'total_operations': sum(self.metrics.values())
        }

Redis-specific Monitoring:
class RedisMonitor:
    def __init__(self, redis_client):
        self.redis = redis_client
    
    def get_info(self):
        """Get Redis server information"""
        info = self.redis.info()
        return {
            'used_memory': info['used_memory'],
            'used_memory_human': info['used_memory_human'],
            'used_memory_peak': info['used_memory_peak'],
            'connected_clients': info['connected_clients'],
            'total_commands_processed': info['total_commands_processed'],
            'keyspace_hits': info['keyspace_hits'],
            'keyspace_misses': info['keyspace_misses'],
            'expired_keys': info['expired_keys'],
            'evicted_keys': info['evicted_keys']
        }
    
    def get_hit_ratio(self):
        """Calculate Redis hit ratio"""
        info = self.redis.info()
        hits = info['keyspace_hits']
        misses = info['keyspace_misses']
        total = hits + misses
        
        if total == 0:
            return 0
        return hits / total
    
    def get_memory_usage(self):
        """Get detailed memory usage"""
        memory_info = self.redis.memory_usage()
        return memory_info

Alerting System:
class CacheAlerting:
    def __init__(self, monitor, thresholds):
        self.monitor = monitor
        self.thresholds = thresholds
    
    def check_alerts(self):
        """Check for alert conditions"""
        stats = self.monitor.get_stats()
        alerts = []
        
        # Hit ratio too low
        if stats['hit_ratio'] < self.thresholds['min_hit_ratio']:
            alerts.append({
                'type': 'low_hit_ratio',
                'value': stats['hit_ratio'],
                'threshold': self.thresholds['min_hit_ratio']
            })
        
        # Error rate too high
        error_rate = stats['errors'] / stats['total_operations']
        if error_rate > self.thresholds['max_error_rate']:
            alerts.append({
                'type': 'high_error_rate',
                'value': error_rate,
                'threshold': self.thresholds['max_error_rate']
            })
        
        return alerts

# Usage
thresholds = {
    'min_hit_ratio': 0.8,
    'max_error_rate': 0.01
}
alerting = CacheAlerting(monitor, thresholds)
alerts = alerting.check_alerts()`}
          </pre>
        </section>

        <section>
          <h2 style={styles.heading}>Best Practices</h2>
          <ul style={styles.list}>
            <li style={styles.listItem}>
              <strong>Choose the right tool:</strong> Use Redis for complex data structures and advanced features, 
              Memcached for simple key-value caching with maximum performance.
            </li>
            <li style={styles.listItem}>
              <strong>Set appropriate TTLs:</strong> Configure time-to-live values based on data volatility and 
              business requirements to balance freshness and performance.
            </li>
            <li style={styles.listItem}>
              <strong>Implement cache warming:</strong> Proactively populate cache with frequently accessed data 
              to avoid cache misses during peak traffic.
            </li>
            <li style={styles.listItem}>
              <strong>Monitor cache performance:</strong> Track hit ratios, memory usage, and response times to 
              optimize cache configuration and identify issues.
            </li>
            <li style={styles.listItem}>
              <strong>Plan for cache failures:</strong> Implement fallback mechanisms and circuit breakers to 
              handle cache unavailability gracefully.
            </li>
            <li style={styles.listItem}>
              <strong>Use efficient serialization:</strong> Choose serialization formats like MessagePack for 
              better performance and smaller memory footprint.
            </li>
          </ul>
        </section>

        <section>
          <h2 style={styles.heading}>Key Takeaways</h2>
          <p style={styles.paragraph}>
            Application-level caching with Redis and Memcached can dramatically improve application performance 
            by reducing database load and serving frequently accessed data from memory. The key is choosing the 
            right caching solution and implementing appropriate patterns for your use case.
          </p>
          <p style={styles.paragraph}>
            Remember that caching introduces complexity in terms of data consistency and invalidation. Design your 
            caching strategy carefully, monitor performance metrics continuously, and always have fallback mechanisms 
            in place to handle cache failures gracefully.
          </p>
        </section>
      </main>
    </article>
  );
};

export default AppLevelCache;
